import { logger } from '@shared/utils/logger';
import type { ReactNode } from 'react';
import { Component } from 'react';

import ErrorPage from './error-page';
import { captureRuntimeErrorInPostHog } from '@core/api/capture-runtime-error-in-posthog';

interface Props {
  children: ReactNode;
  /** Changing this clears a caught error so the next screen gets to render.
   *
   * A PROP, deliberately, and not a `key` on this element: keying the boundary
   * on the location threw away and rebuilt the entire authenticated tree on
   * every navigation — including panes whose scroll position is part of the
   * screen, like the inbox's thread list, which reset to the top the moment a
   * conversation was opened. Resetting from inside means the tree survives a
   * navigation that did not error, while a navigation AWAY from a crashed
   * screen still recovers, which is the whole reason the reset exists.
   */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  resetKey?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    logger.error(error?.message, error);
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: Props, state: State): State | null {
    if (props.resetKey === state.resetKey) return null;
    return { hasError: false, error: undefined, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureRuntimeErrorInPostHog(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage error={this.state.error} />;
    }

    return this.props.children;
  }
}
